import { Injectable, OnDestroy, RendererFactory2 } from '@angular/core'
import { PageMetaLinkService } from '@spartacus/storefront'
import { BaseSiteService, CmsService, WindowRef } from '@spartacus/core'
import { Router } from '@angular/router'
import { CountryLanguageService } from '../../site-context/facade/country-language.service'
import { MbConstants } from '../../shared/mb-constants'
import { combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs'
import { first } from 'rxjs/operators'

@Injectable({
  providedIn: 'root'
})
export class MbPageMetaLinkService extends PageMetaLinkService implements OnDestroy {

  isInitialized: boolean = false
  subscription = new Subscription()

  constructor (winRef: WindowRef,
               rendererFactory: RendererFactory2,
               private router: Router,
               private countryLanguageService: CountryLanguageService,
               private baseSiteService: BaseSiteService,
               private cmsService: CmsService) {
    super(winRef, rendererFactory)
  }

  setHrefLang (): Observable<boolean> {
    forkJoin([
      this.countryLanguageService.getActive().pipe(first()),
      this.baseSiteService.getAll().pipe(first()),
      this.cmsService.getCurrentPage().pipe(first())
    ]).subscribe(([currentLanguage, countries, page]) => {
      countries.filter((object: any) => object?.country?.isocode && object?.defaultLanguage?.isocode).forEach((shop: any) => {
        const country = shop?.country?.isocode
        const language = shop?.defaultLanguage?.isocode
        if (!this.winRef.location?.href?.includes(MbConstants.CHECKOUT)) {
          const content = `${language}-${country}`
          let link: any
          if (this.isInitialized) {
            try {
              link = this.renderer.selectRootElement('link[rel="alternate"]', true)
            } catch (error: any) {
              console.warn('The selector link[rel="alternate"] did not match any elements')
              link = this.renderer.createElement('link')
            }
          } else {
            link = this.renderer.createElement('link')
          }
          link.rel = 'alternate'
          link.hreflang = content
          let ptSeoUrl
          ptSeoUrl = page?.properties?.formData?.pt_seoUrl?.value
          if (page?.pageId === 'homepage') {
            ptSeoUrl = '/'
          } else {
            ptSeoUrl = ptSeoUrl?.length ? ptSeoUrl.startsWith('/') ? ptSeoUrl : `/${ptSeoUrl}` : ''
          }
          if (!ptSeoUrl) {
            const currentHref = this.router.url.split(currentLanguage + '/')?.[1] ?? ''

            /**
             * TODO this regex is an attempt to better recognize the search page than previous implementations.
             * We were having false positives recognizing the search (https://mbdigitalbusiness.atlassian.net/browse/CWT-782).
             * Please note that this is URL matching is not an approach that should be taken at all. Pages that
             * have specific requirements on resolving these alternate links should bring their own resolver
             * in a configurable, declarative way. This should be refactored to make such a solution possible.
             */
            const isSearchPageRegEx = /search\/|\/search$/
            const isSearchPage = !!this.winRef.location?.pathname?.match(isSearchPageRegEx)?.length

            if (isSearchPage) {
              const url = currentHref.substring(0, currentHref.lastIndexOf('/'))
              link.href = `${this.winRef.location?.origin}/${country}-${language}/${url}`
            } else {
              const url = currentHref.includes('#') ? currentHref.substring(0, currentHref.indexOf('#')) : currentHref
              link.href = `${this.winRef.location?.origin}/${country}-${language}/${url}`
            }
          } else {
            link.href = `${this.winRef.location?.origin}/${country}-${language}${ptSeoUrl}`
          }
          this.renderer.appendChild(this.winRef.document.head, link)
        } else {
          if (this.isInitialized) {
            const content = `${language}-${country}`
            try {
              const link = this.renderer.selectRootElement(`link[hreflang=${content}]`, true)
              this.renderer.removeChild(this.winRef.document.head, link)
            } catch (error: any) {
              console.warn('The selector ' + `link[hreflang=${content}]` + ' did not match any elements')
            }
          }
        }
      })
      this.isInitialized = !this.winRef.location?.href?.includes(MbConstants.CHECKOUT)
    })

    return of(true)
  }

  setCanonicalUrl (): Observable<string> {
    let canonicalURL = this.winRef?.location?.origin ?? ''
    this.subscription.add(combineLatest([this.countryLanguageService.getActive(), this.cmsService.getCurrentPage()])
      .subscribe(([languageData, page]) => {
        let ptSeoUrl: string = page?.properties?.formData?.pt_seoUrl?.value ?? ''
        ptSeoUrl = ptSeoUrl.split('#').shift() ?? ''
        canonicalURL += `/${languageData}`
        if (page?.pageId === 'homepage') {
          canonicalURL += '/'
          return
        }
        canonicalURL += ptSeoUrl?.length ? ptSeoUrl.startsWith('/') ? ptSeoUrl : `/${ptSeoUrl}` : '/'
      })
    )
    return of(canonicalURL)
  }

  ngOnDestroy (): void {
    this.subscription?.unsubscribe()
  }
}
